/**
 * Modèles d'e-mail — rubrique E-mails.
 *
 * Le catalogue de ce que l'API envoie par e-mail (compte, notifications,
 * bibliothèque de campagnes), la réécriture langue par langue et la
 * duplication d'un modèle en campagne e-mail. Endpoints :
 * /api/marketing/email-templates (administrateurs).
 */
import type { CampaignLang, CampaignText } from './marketingService';

export type TemplateSource = 'account' | 'notification' | 'campaign';

export interface TemplateFields {
  subject: string | null;
  /** E-mails de compte seulement (titre affiché dans l'e-mail). */
  title: string | null;
  html: string | null;
  text: string | null;
}

export interface TemplateGroup {
  id: string;
  label: string;
}

export interface TemplateSummary {
  source: TemplateSource;
  key: string;
  kind?: string;
  group: string;
  label: string;
  editable: boolean;
  customized_langs: CampaignLang[];
  updated_at: string | null;
  updated_by_name: string | null;
}

export interface TemplateLang {
  /** Version du code, rendue avec les données d'exemple. */
  original: TemplateFields;
  /** Point de départ d'une réécriture (texte à trous, ou {{default.…}}). */
  prefill: TemplateFields | null;
  /** Champs où le code compose (dates, branches, listes) : repris par {{default.…}}. */
  computed: (keyof TemplateFields)[];
  variables: string[];
  defaults: string[];
  override: (TemplateFields & { updated_at: string; updated_by_name: string | null }) | null;
}

export interface TemplateDetail {
  source: TemplateSource;
  key: string;
  group: string;
  label: string;
  editable: boolean;
  kind?: string;
  audience?: string;
  link?: string | null;
  langs: Record<CampaignLang, TemplateLang>;
}

export interface CampaignDraft {
  source: TemplateSource;
  key: string;
  label: string;
  kind: string;
  audience: string;
  link: string | null;
  content: Record<CampaignLang, CampaignText>;
  uses_sample_data: boolean;
  unresolved: string[];
}

const root = () => `${process.env.REACT_APP_API_URL}api/marketing`;
const templateUrl = (source: TemplateSource, key: string) => `${root()}/email-templates/${source}/${encodeURIComponent(key)}`;

const request = async <T,>(url: string, token: string, init: RequestInit = {}): Promise<T> => {
  const res = await fetch(url, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(init.headers || {})
    }
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data?.error || `Erreur ${res.status}: ${res.statusText}`);
  }
  return (data?.data ?? data) as T;
};

const requestHtml = async (url: string, token: string, body: unknown): Promise<string> => {
  const res = await fetch(url, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data?.error || `Aperçu indisponible (${res.status})`);
  }
  return res.text();
};

export const getEmailTemplates = (token: string) =>
  request<{ groups: TemplateGroup[]; items: TemplateSummary[] }>(`${root()}/email-templates`, token);

export const getEmailTemplate = (token: string, source: TemplateSource, key: string) =>
  request<TemplateDetail>(templateUrl(source, key), token);

/** Page HTML avec les données d'exemple ; `draft` = brouillon non enregistré. */
export const previewEmailTemplate = (token: string, source: TemplateSource, key: string, lang: CampaignLang, draft?: Partial<TemplateFields> | null) =>
  requestHtml(`${templateUrl(source, key)}/preview`, token, { lang, draft: draft || null });

/** Envoie l'aperçu à l'adresse du compte connecté ; renvoie l'adresse. */
export const sendEmailTemplateTest = (token: string, source: TemplateSource, key: string, lang: CampaignLang, draft?: Partial<TemplateFields> | null) =>
  request<{ to: string }>(`${templateUrl(source, key)}/test`, token, { method: 'POST', body: JSON.stringify({ lang, draft: draft || null }) });

export const saveEmailTemplate = (token: string, source: TemplateSource, key: string, lang: CampaignLang, fields: Partial<TemplateFields>) =>
  request<TemplateDetail>(`${templateUrl(source, key)}/${lang}`, token, { method: 'PUT', body: JSON.stringify(fields) });

/** Retour au texte d'origine (celui du code) dans une langue. */
export const resetEmailTemplate = (token: string, source: TemplateSource, key: string, lang: CampaignLang) =>
  request<TemplateDetail>(`${templateUrl(source, key)}/${lang}`, token, { method: 'DELETE' });

export const getCampaignDraft = (token: string, source: TemplateSource, key: string) =>
  request<CampaignDraft>(`${templateUrl(source, key)}/campaign-draft`, token);

/** Aperçu d'une campagne pas encore enregistrée. */
export const previewCampaignContent = (token: string, content: Record<CampaignLang, CampaignText>, lang: CampaignLang) =>
  requestHtml(`${root()}/campaigns/preview`, token, { content, lang });
